import { Injectable, Signal, WritableSignal, computed, signal } from "@angular/core";
import { TabOrderData, TabOrderModel } from "../../shared/models/tab-order.model";
import { Addon, AddonType } from "ngx-exdr-stmx-common";
import { Observable, map, of, switchMap } from "rxjs";
import { CustomDomainService } from "../../shared/services/custom-domain.service";
import { defaultTabs, TabKeyEnum, SubTypeEnum } from "ngx-exdr-stmx-common";
import { Router } from "@angular/router";

@Injectable({ providedIn: 'root' })
export class TabsManagementService {
    tabs: WritableSignal<TabOrderData[] | undefined> = signal(undefined);
    homeAddon: WritableSignal<Addon> = signal(null);
    showAllTabs: WritableSignal<boolean> = signal(false);     

    constructor(
        private customDomainService: CustomDomainService,
        private router: Router
    ){
        
    }

    /**
     * Set Tabs signal to use it render tabs in header 
     * @returns Observable<TabOrderData[]>
     */
    initTabs() : Observable<TabOrderData[]>{
        if(this.tabs() && this.tabs().length > 0){
            return of(this.tabs());
        } else {
            return this.customDomainService.getAddons().pipe(
                switchMap(addons => {
                    const tabOrderAddon = addons.find(addon => addon.Type == AddonType.TabOrder);
                    if (tabOrderAddon && tabOrderAddon.PublicProperties) {
                    tabOrderAddon.PublicProperties.parsedData = JSON.parse(tabOrderAddon.PublicProperties.jsonData);
                    }
                    let publicTabs: TabOrderData[] | undefined = [];
                    if (tabOrderAddon && tabOrderAddon?.PublicProperties && tabOrderAddon?.PublicProperties.parsedData) {
                    publicTabs = this.filterTabs(tabOrderAddon?.PublicProperties.parsedData.data ?? []); 
                    publicTabs?.sort((a, b) => a.order - b.order);
                    }
                    this.formatTabsNavigationPath(publicTabs ?? [], addons);

                    // console.log("TAB ORDER", publicTabs);
                    // console.log(tabOrderAddon?.PublicProperties?.parsedData);
                    let homeAddon = addons.find(addon => addon.PublicProperties.isLandingPageTab == "1" || addon.PublicProperties.isLandingPageTab == true) ?? null; 
                    this.homeAddon.set(homeAddon);
                    let home = publicTabs?.find(tab => tab.data.name && tab.data.name.toLowerCase() == TabKeyEnum.Home) ?? null;
                    
                    publicTabs = this.configureGroupTabs(publicTabs);
                    // console.log(publicTabs);

                    this.tabs.set(publicTabs.filter(tab => !tab.data.name || tab.data.name.toLowerCase() != TabKeyEnum.Home));

                    return of(publicTabs);
                })
            )
        }
    }
	
	configureGroupTabs(tabs: any) {
		const groupTabs = tabs.filter((tab) => tab?.data?.subtype == SubTypeEnum.NAV_GROUP);
	
		groupTabs.map((tab: any) => {
			tab.data.subTabs = tab?.data?.subTabs
				?.map((key, index) => {
					const subTab: any = tabs.find(
						(tab) => {
							return tab.key == key && (tab.isvisible || (this.showAllTabs() && tab?.viewOn !== ''))
						}
					);
					if (subTab) {
						subTab.isvisible = tab.isvisible;
					}
					tabs = tabs.filter((tab) => tab.key !== key);
					return subTab;
				})
				.filter((tab) => {
					return !!tab && (tab.isvisible || (this.showAllTabs() && tab?.viewOn !== ''))
				});
	
			return tab;
		}) || [];
		return tabs.filter((tab) => !(tab?.data?.subtype == SubTypeEnum.NAV_GROUP && !tab?.data?.subTabs?.length));
	}

    getHeaderTabs() : Observable<TabOrderData[]> {
        return this.initTabs();
    }
    
    getHeaderTabsFiltered(filter?: { key?: string; excluded?: TabKeyEnum[] }): Observable<TabOrderData[]> {
        return this.getHeaderTabs().pipe(
          map(tabs => tabs.filter(tab => {
            const keyMatch = filter?.key ? tab.key === filter.key : true;
            const excludedMatch = filter?.excluded ? !filter.excluded.includes(tab.key as TabKeyEnum) : true;
            return keyMatch && excludedMatch;
          }))
        );
    }

    async getActiveTab(): Promise<TabOrderData| null> {
        // console.log('Current URL:', this.router.url);
        const moreTabs  = await this.getHeaderTabs().toPromise()                
        for (const t of moreTabs) {
          if (t.data?.subtype !== SubTypeEnum.WEB_LINK) {
            // console.log('Checking tab:', t.data?.url);      
            if (t.data?.subTabs?.length) {                
                for (const st of t.data.subTabs) {
                    // console.log('Checking sub-tab:', st.data?.url);      
                    if (st.data?.subtype !== SubTypeEnum.WEB_LINK && this.router.url.includes(st.data?.url)) {
                        console.log('Matched sub-tab key:', t.key);
                        return st;
                    }
              }
            } else if (this.router.url.includes(t.data?.url)) {
            //   console.log('Matched tab key:', t.key);
              return t;
            }
          }
        }
        return null;
    }

    /**
     * Filter tabs by viewOn and visibleBy properties 
     * if tab's viewOn includes 'web' and visibleBy includes 'anonymous'
     * @param data TabOrderData[]
     * @returns 
     */
    filterTabs(data: TabOrderData[]): TabOrderData[] {
        return data.filter((tab) => (tab.viewOn && tab.viewOn.toLowerCase().includes("web")) && this.filterVisibleBy(tab.visibleBy, 'anonymous'));
    }
    /**
     * Check if visibleBy array has property named filterFor
     * @param visibleBy 
     * @param filterFor anonymous or groupId  
     * @returns 
     */
    filterVisibleBy(visibleBy: { [key: string]: string }[], filterFor: string): boolean {
        if(!visibleBy) {
            return true;
        } else if (!visibleBy.length){
            return false; 
        } else {
            return visibleBy.some(item => item.hasOwnProperty(filterFor));
        }
    }

    /**
     * Filter addons by urlName - urlName is unique for each custom addon
     * @param urlName 
     * @returns 
     */
    filterByUrlName(urlName: string): Observable<Addon | undefined> {
        return this.customDomainService.getAddons().pipe(
            //filter addons by urlName
            map((addons: Addon[]) => {
                return addons.find((addon) => addon.PublicProperties.urlName === urlName);
            })
        )
    }
    
    /**
     * Method is used to read information from addons and format the tabs navigation path
     * @param publicTabs 
     * @param addons 
     */
    formatTabsNavigationPath(publicTabs: TabOrderData[], addons: Addon[]): void {
        publicTabs.forEach((tab) => {
            if(tab.key.includes('custom')) {
                const key = tab.key.replace('custom tab', '');
                const customTabAddon = addons.find((addon) => addon.InstanceId === key);
                if(customTabAddon){
                    switch(tab.data.subtype){
                        case (SubTypeEnum.TOPIC):  
                            tab.data.url = customTabAddon.PublicProperties.urlName + '-lb';
                            break;
                        case (SubTypeEnum.IFRAME):
                            tab.data.url = customTabAddon.PublicProperties.urlName + '-i';
                            break;
                        case (SubTypeEnum.BLOG):
                            tab.data.url = customTabAddon.PublicProperties.urlName + '-b';
                            break;
                        case (SubTypeEnum.FEEDS):
                            tab.data.url = customTabAddon.PublicProperties.urlName + '-f';
                            break;
                        default:
                            tab.data.url = customTabAddon.PublicProperties.urlName;
                    }

                    tab.data.tabLayout = customTabAddon.PublicProperties.tabLayout;
                    tab.data.tabDescription = customTabAddon.PublicProperties.tabDescription;
                    
                }
            } else {
                if(!tab.data){
                    tab.data = { subtype: SubTypeEnum.NONE, url: '' };
                }
                tab.data.url = defaultTabs[tab.key as TabKeyEnum]; //this url will be used in HTML to set routerLink of nav item 
                if(tab.key as TabKeyEnum == TabKeyEnum.Activities){
                    tab.data.isExternalLink = true; 
                }
            }
        })
    }


    async isActiveSubTab(): Promise<any> {
        // console.log('Current URL:', this.router.url);
        const moreTabs  = await this.getHeaderTabs().toPromise()        
        
        for (const t of moreTabs) {
          if (t.data?.subtype !== SubTypeEnum.WEB_LINK) {
            // console.log('Checking tab:', t.data?.url);      
            if (t.data?.subTabs?.length) {                
                for (const st of t.data.subTabs) {
                    // console.log('Checking sub-tab:', st.data?.url);      
                    if (st.data?.subtype !== SubTypeEnum.WEB_LINK && this.router.url.includes(st.data?.url)) {
                        console.log('Matched sub-tab key:', t.key);                        
                        const layoutKey = await this.checkLayout(t)
                        return {isActive: true,layoutKey:layoutKey, parentTab: (layoutKey == 'innertab') ?  t : undefined};
                        
                    }
              }
            } else if (this.router.url.includes(t.data?.url)) {
                //   console.log('Matched tab key:', t.key);
                const layoutKey = await this.checkLayout(t)
                return {isActive: false,layoutKey:layoutKey, parentTab: (layoutKey == 'innertab') ?  t : undefined};
            }
          }
        }        
        return {isActive: false,layoutKey:'navigation', parentTab: undefined};        
    }

    checkLayout(tab: any): string | null {
        // Check if 'data' and 'tabLayout' property exists in the 'data' object and return its value or null
        // If 'tabLayout' is not defined, default to null.    
        const defaultLayout = tab.data.tabLayout || null;    
        // Check if 'subTabs' exists and is an array
        if (Array.isArray(tab.data.subTabs) && tab.data.subTabs.length > 0) {
          // Check if at least one subTab does not have a subtype of 5      
          const hasNonType5SubTab = tab.data.subTabs.some(subTab => subTab.data?.subtype !== 5);
          // If all subTabs have subtype 5, override the layout to 'navigation'
          if (!hasNonType5SubTab) {        
            return 'navigation';
          }
        }
        return defaultLayout;
      }

}